import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { TID } from "@/testIds";
import { toast } from "sonner";
import { Plus, Trash2, Phone, Pencil } from "lucide-react";

export default function CoachesPage() {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCoaches(await api.listCoaches());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (c) => {
    if (!window.confirm(`Remove coach ${c.full_name}?`)) return;
    try {
      await api.deleteCoach(c.id);
      toast.success("Coach removed");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-6 relative">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900">Coaches</h1>
          <p className="text-slate-500 text-sm mt-1">
            {coaches.length} coach{coaches.length === 1 ? "" : "es"}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {loading ? (
          <div className="text-center text-slate-500 py-10">Loading...</div>
        ) : coaches.length === 0 ? (
          <div className="text-center text-slate-500 py-16 bg-slate-50 rounded-xl border border-slate-100">
            No coaches yet. Add your first coach.
          </div>
        ) : (
          coaches.map((c) => (
            <div
              key={c.id}
              data-testid={TID.coachRow(c.id)}
              className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-3"
            >
              <div className="w-11 h-11 rounded-full bg-[#FF6B6B] text-white flex items-center justify-center font-bold shrink-0">
                {c.full_name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-slate-900 truncate">{c.full_name}</p>
                <p className="text-xs text-slate-500 flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {c.phone_number}
                </p>
              </div>
              <Link
                to={`/coaches/${c.id}/edit`}
                data-testid={TID.coachEditBtn(c.id)}
                className="w-9 h-9 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900 flex items-center justify-center"
                title="Edit coach"
              >
                <Pencil className="w-4 h-4" />
              </Link>
              <button
                data-testid={TID.coachDeleteBtn(c.id)}
                onClick={() => remove(c)}
                className="w-9 h-9 rounded-full text-slate-500 hover:bg-red-50 hover:text-red-600 flex items-center justify-center"
                title="Remove coach"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))
        )}
      </div>

      <Link
        to="/coaches/new"
        data-testid={TID.coachesFabAdd}
        className="fixed bottom-24 md:bottom-8 right-6 md:right-8 z-30 w-14 h-14 rounded-full bg-[#0A2540] hover:bg-[#081D33] text-white flex items-center justify-center shadow-lg"
        title="Add coach"
      >
        <Plus className="w-6 h-6" />
      </Link>
    </div>
  );
}
